<template>
  <view class="c-preview" v-if="appStore.resourcePreviewerOptions.data.length > 0">
    <view
      class="mask"
      :class="{ 'mask--fixed': appStore.resourcePreviewerOptions.display }"
      @click="handleMaskClick"
    >
      <view class="nav-wrap">
        <view class="nav-wrap__fill" :style="navFillStyle" />
        <view class="nav-wrap__content" :style="navContentStyle">
          <view class="mask-close">
            <c-img :size="48" remote-name="close_1" />
          </view>

          <view class="mask-dots">
            {{ swiperIndexRef + 1 }} / {{ appStore.resourcePreviewerOptions.data.length }}
          </view>
        </view>
      </view>
    </view>

    <view class="swiper__outer">
      <swiper
        class="swiper__comp"
        :autoplay="false"
        :current="swiperIndexRef"
        @change="handleSwiperChange"
      >
        <template v-for="item in appStore.resourcePreviewerOptions.data" :key="item.url">
          <c-img
            v-if="item.type === 'video' && !isPlayRef"
            class="source-button"
            remote-name="resource_play"
          />

          <swiper-item class="swiper__item">
            <view
              class="progress-outer"
              :class="{ 'progress-outer--fixed': appStore.resourcePreviewerOptions.display }"
              v-if="item.type === 'video'"
            >
              <progress
                class="progress"
                :percent="playTimeRef"
                :stroke-width="3"
                border-radius="12"
                activeColor="#ef6348"
                backgroundColor="#e0dede"
              />
            </view>

            <video
              v-if="item.type === 'video'"
              id="myVideo"
              class="swiper__item"
              :src="item.url"
              :controls="false"
              :show-center-play-btn="false"
              :show-progress="false"
              :enable-progress-gesture="false"
              @error="handleSourceError"
              @ended="handleSourceEnded"
              @pause="handleSourcePause"
              @play="handleSourcePlay"
              @timeupdate="handleSourceTimeUpdate"
            />

            <c-img
              v-else
              class="swiper__item"
              :img-mode="'aspectFit'"
              :src="item.url || item.imgUrl"
            />
          </swiper-item>
        </template>
      </swiper>
    </view>
  </view>
</template>

<script setup lang="ts">
import { CLOSE_EVENT } from '@/constants';
import { useResourcePreview } from '@/hooks';
import { useAppStore } from '@/store';
import type { AnyObj } from '@/types';
import { commonUtils } from '@/utils';
import { computed, getCurrentInstance, nextTick, onMounted, ref, watch } from 'vue';

const emits = defineEmits([CLOSE_EVENT]);

const appStore = useAppStore();

const instance = getCurrentInstance();

const swiperIndexRef = ref(0);

const systemInfo = commonUtils.navbar.get();

const rpHook = useResourcePreview();

const navFillStyle = computed(() => ({ height: `${systemInfo.menuButtonInfo.top}px` }));
const navContentStyle = computed(() => ({ height: `${systemInfo.menuButtonInfo.height}px` }));

// 播放进度
const playTimeRef = ref(0);
// swiper切换时，保存已有播放进度，切换回video时继续播放
// let playTimeCache = 0;
// 资源上下文
const sourceCtx = ref<UniApp.VideoContext>();
// 是否播放状态
const isPlayRef = ref(false);

const handleMaskClick = () => {
  rpHook.resourcePreview({
    data: [],
    defaultIndex: 0,
    display: false,
  });
  emits(CLOSE_EVENT);
};

/** 监听轮播滚动变化 */
const handleSwiperChange = (e: { detail: { current: number } }) => {
  const current = e.detail.current;
  swiperIndexRef.value = current;

  if (current === 0) {
    sourceCtx.value?.play();
  } else {
    sourceCtx.value?.pause();
  }
};

/** 视频加载错误 */
const handleSourceError = () => {
  console.log('<资源加载错误>');
};

/** 视频播放结束 */
const handleSourceEnded = () => {
  playTimeRef.value < 100 && (playTimeRef.value = 100);
  setTimeout(() => {
    sourceCtx.value?.play();
  }, 300);
};

/** 视频播放中断、停止 */
const handleSourcePause = () => {
  isPlayRef.value = false;
};

/** 视频播放开始 */
const handleSourcePlay = () => {
  isPlayRef.value = true;
};

/** 播放时间更新 */
const handleSourceTimeUpdate = (e: AnyObj) => {
  const { duration, currentTime } = e.detail;
  const percent = Math.ceil((currentTime / duration) * 100);
  playTimeRef.value = percent > 100 ? 100 : percent;
};

watch(
  () => appStore.resourcePreviewerOptions.defaultIndex,
  (index) => {
    if (index !== undefined && index >= 0) {
      swiperIndexRef.value = index;
    }
  }
);

onMounted(async () => {
  await nextTick();

  const ctx = uni.createVideoContext('myVideo', instance);
  if (ctx) {
    sourceCtx.value = ctx;
    appStore.resourcePreviewerOptions.defaultIndex === 0 && ctx.play();
  }
});
</script>

<style scoped lang="scss">
.c-preview {
  position: relative;
  z-index: 9999;

  .mask {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 1);
    z-index: -1;
    opacity: 0;
    // transition: all 300ms ease;

    .nav-wrap {
      position: relative;
      width: 100%;
    }

    .nav-wrap__fill {
      width: 100%;
    }

    .nav-wrap__content {
      position: relative;
      width: 100%;
    }

    &.mask--fixed {
      z-index: 999;
      opacity: 1;
    }

    .mask-close {
      position: absolute;
      top: 50%;
      left: 20px;
      transform: translateY(-50%);
    }

    .mask-dots {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 24rpx;
      font-weight: bold;
      color: $W;
    }
  }

  .swiper__outer {
    position: fixed;
    top: 50%;
    left: 50%;
    z-index: 1002;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 75vh;

    .swiper__comp {
      width: 100%;
      height: 100%;
    }
    .swiper__item {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
    }
  }

  .progress-outer {
    position: absolute;
    bottom: 0rpx;
    left: 50%;
    z-index: 999;
    transform: translateX(-50%);
    width: 100%;
  }

  .source-button {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 99;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 140rpx;
    height: 140rpx;
    border-radius: 50%;
  }
}
</style>
